import React, { useEffect, useState } from "react";
import IntlMessages from "util/IntlMessages";
import {
  Card,
  Table,
  Col,
  Button,
  Upload,
  Row,
  Form,
  Modal,
  message,
  Select,
  Spin,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { UploadOutlined, MedicineBoxOutlined } from "@ant-design/icons";
import { uploadMOMPDF, getMOMList } from "../../../appRedux/reducers/MOMSlice";
import { getRole } from "../../../helpers";
import CustomPagination from "../../../components/CustomPagination";
import LoadingBar from "../../../components/LoadingBar";

const MOMList = (props) => {
  const dispatch = useDispatch();
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserLegal, setIsUserLegal] = useState(false);
  const [requestObj, setRequestObj] = useState({
    limit: 10,
    offset: 0,
    quarter_id: undefined,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [currentQuarter, setCurrentQuarter] = useState("");
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(true);
  const [form] = Form.useForm();
  const [quarter, setQuarter] = useState([]);
  const [selectedQuarterId, setSelectedQuarterId] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const { Option } = Select;

  // FIXED: Use correct Redux state path for RTK slice
  const isLoaded = useSelector((state) => state.momList.isLoaded);
  const momListData = useSelector((state) => state.momList.MOMList);

  // Set role
  useEffect(() => {
    setIsUserAdmin(() => {
      return getRole() === "ADMIN";
    });
    setIsUserLegal(() => {
      return getRole() === "LEGAL";
    });

    dispatch(getMOMList(requestObj));
  }, [requestObj, dispatch]);

  // Set Quarter
  useEffect(() => {
    if (isLoaded && momListData && quarter.length === 0) {
      let dataArray = [];
      if (momListData.results) {
        dataArray = momListData.results;
      } else if (Array.isArray(momListData)) {
        dataArray = momListData;
      }

      const uniqueQuarters = Array.from(
        new Map(
          dataArray
            .filter((v) => v.quarter_name)
            .map((v) => [v.quarter_id, { key: v.quarter_id, name: v.quarter_name }])
        ).values()
      );

      setQuarter(uniqueQuarters); // <-- only set once
    }
  }, [isLoaded, momListData, quarter.length]);


  // Set data
  useEffect(() => {
    if (isLoaded && momListData) {
      let finaldata = momListData;

      // Set total count for pagination
      if (momListData.count !== undefined) {
        setTotalCount(momListData.count);
      } else if (Array.isArray(momListData)) {
        setTotalCount(momListData.length);
      }

      // Check API response structure - Updated for new format
      if (momListData.results && Array.isArray(momListData.results)) {
        // New API structure: results is directly an array
        finaldata = momListData.results;
      } else if (momListData.results && momListData.results.rows) {
        // Legacy API structure: results.rows (fallback)
        finaldata = momListData.results.rows;
      } else if (Array.isArray(momListData)) {
        finaldata = momListData;
      } else {
        finaldata = [];
      }

      let MOMData = finaldata
        ? finaldata.map((value) => ({
            key: value.quarter_id,
            template_name: value.quarter_name,
            quarter_id: value.quarter_id,
            document:
              value.mom_docs.length > 0
                ? [
                    value.mom_docs.length > 0 ? (
                      <a
                        href={value.mom_docs[0].document}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {value.mom_docs[0].name}
                      </a>
                    ) : (
                      ""
                    ),
                    value.mom_docs.length > 1 ? (
                      <a
                        href={value.mom_docs[1].document}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {value.mom_docs[1].name}
                      </a>
                    ) : (
                      ""
                    ),
                    value.mom_docs.length > 2 ? (
                      <a
                        href={value.mom_docs[2].document}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {value.mom_docs[2].name}
                      </a>
                    ) : (
                      ""
                    ),
                  ]
                : [],
          }))
        : [];
      let newData = [];
      MOMData.forEach((data) => {
        if (data.document.length > 0) {
          data.document.forEach((value) => {
            newData.push({
              key: data.quarter_id,
              template_name: data.template_name,
              quarter_id: data.quarter_id,
              document: value,
            });
          });
        } else {
          // FIXED: Include quarters with no documents to show in table
          newData.push({
            key: data.quarter_id,
            template_name: data.template_name,
            quarter_id: data.quarter_id,
            document: "No documents uploaded",
          });
        }
      });

      setData(newData);
    }
  }, [isLoaded, momListData]);

  const handleTableChange = (pagination, filters) => {
    const current = pagination?.current || 1;
    const pageSize = pagination?.pageSize || requestObj.limit || 10;

    setFilteredInfo(filters);

    const selectedQuarterName = filters.template_name?.[0];
    const selectedQuarter = quarter.find((q) => q.name === selectedQuarterName);

    setRequestObj({
      limit: pageSize,
      offset: (current - 1) * pageSize,
      quarter_id: selectedQuarter?.key || undefined,
    });
  };



  // Handle cancel request
  const handleCancel = () => {
    setIsModalVisible(false);
    setUploadStatus(true);
  };

  // Handle modal open
  const showModal = () => {
    setIsModalVisible(true);
  };

  // Handle request change
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    if (e.fileList && e.fileList.length > 1) {
      return [e.fileList[1]];
    }

    return e && e.fileList;
  };

  // Merge Cells
  const mergeCell = (data) => {
    if (!data || !Array.isArray(data)) return [];

    const output_res = data
      .reduce((result, item) => {
        if (result.indexOf(item.template_name) < 0) {
          result.push(item.template_name);
        }
        return result;
      }, [])
      .reduce((result, template_name) => {
        const children = data.filter(
          (item) => item.template_name === template_name,
        );
        result = result.concat(
          children.map((item, index) => ({
            ...item,
            rowSpan: index === 0 ? children.length : 0,
          })),
        );
        return result;
      }, []);
    return output_res;
  };

  // Dummy Request
  const dummyRequest = async (options) => {
    const { onSuccess, onError, file } = options;
    setTimeout(() => {
      const isLt2M = file.size / 1024 / 1024 < 15;
      if (!isLt2M) {
        message.error("File must be smaller than 15MB!");
        onError("error");
      } else {
        setUploadStatus(false);
        onSuccess("ok");
      }
    }, 0);
  };

  // Set Quarter Id
  const setQuarterId = (e) => {
    setCurrentQuarter(e);
  };

  // Upload request
  const fileUpload = (event) => {
    if (event && event.upload) {
      const formData = new FormData();
      formData.append("name", event.upload[0].name);
      formData.append("quarter_id", currentQuarter);
      formData.append("document", event.upload[0].originFileObj);
      // dispatch(uploadMOMPDF(formData));
      setIsModalVisible(false);
    } else {
      console.log("File is not uploaded");
    }
  };

  // Columns
  const columns = [
    {
      title: "Quarter Name",
      dataIndex: "template_name",
      key: "template_name",
      width: "300px",
      align: "center",
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            style={{ minWidth: 200, marginBottom: 8, display: 'block' }}
            placeholder="Select a quarter"
            value={filteredInfo.template_name?.[0] || undefined}
            onChange={(value) => {
              const selected = quarter.find(q => q.name === value);
              confirm();
              setFilteredInfo({ template_name: [value] });
              setRequestObj((prev) => ({
                ...prev,
                offset: 0,
                quarter_id: selected?.key,
              }));
            }}
            allowClear
          >
            {quarter.map((q) => (
              <Select.Option key={q.key} value={q.name}>
                {q.name}
              </Select.Option>
            ))}
          </Select>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
              setFilteredInfo({});
              setRequestObj((prev) => ({
                ...prev,
                offset: 0,
                quarter_id: undefined,
              }));
            }}
            type="link"
            size="small"
            style={{ padding: 0 }}
          >
            Reset
          </Button>
        </div>
      ),
      filteredValue: filteredInfo.template_name || null,
      render(_, row) {
        return {
          children: row.template_name,
          props: {
            rowSpan: row.rowSpan,
          },
        };
      },
    },
    {
      title: "File",
      dataIndex: "document",
      key: "document",
      align: "center",
    },
];


  return (
    <div>
      <Row>
        <Col span={24}>
          <div
            className="gx-flex-row"
            style={{ justifyContent: "space-between" }}
          >
            <h1 className="title gx-mb-4">
              <IntlMessages id="sidebar.MOM" />
            </h1>
            <div>
              <Button
                id="add-product-btn"
                type="primary"
                className="gx-ml-auto"
                onClick={() => {
                  showModal();
                }}
              >
                <MedicineBoxOutlined /> Upload MOM
              </Button>
            </div>
          </div>
        </Col>
        {!isLoaded ? (
          <LoadingBar loading={!isLoaded} />
        ) : (
        <div className="gx-w-100">
          <Table
            className="gx-table-responsive mpp-list-table"
            columns={columns}
            dataSource={mergeCell(data)}
            bordered
            pagination={false}
            onChange={handleTableChange}
          />

          {/* Custom Pagination */}
          {totalCount > 0 && (
            <CustomPagination
              current={Math.floor((requestObj.offset || 0) / (requestObj.limit || 10)) + 1}
              total={totalCount}
              pageSize={requestObj.limit || 10}
              showSizeChanger={true}
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={(page, pageSize) => {
                const newPage = pageSize !== requestObj.limit ? 1 : page;
                const newOffset = (newPage - 1) * pageSize;
                setRequestObj(prev => ({
                  ...prev,
                  limit: pageSize,
                  offset: newOffset,
                }));
              }}
            />
          )}
        </div>
        )}
      </Row>
      <Modal
        destroyOnClose={true}
        title={`Upload MOM`}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          name="uploadSignedDocument"
          onFinish={fileUpload}
          preserve={false}
        >
          <Form.Item
            name="quarter"
            label="Quarter"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Select onChange={setQuarterId}>
              {quarter.map((user) => (
                <Option key={user.key} value={user.key}>{user.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="upload"
            label="Upload"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload
              customRequest={dummyRequest}
              listType="picture"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload (Max: 1)</Button>
            </Upload>
          </Form.Item>

          <div className="mb-10">
            <small className="ant-upload-hint">
              **Only supports file size upto 15MB.
            </small>
          </div>
          <Form.Item
            wrapperCol={{
              span: 12,
              offset: 6,
            }}
          >
            <Button type="primary" htmlType="submit" disabled={uploadStatus}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MOMList;